// Ory Kratos Error Handling - follows patterns from kratos-selfservice-ui-react-nextjs
import { AxiosError } from 'axios';
import { Dispatch, SetStateAction } from 'react';
import { NavigateFunction } from 'react-router-dom';

type FlowType = 'login' | 'registration' | 'settings' | 'recovery' | 'verification';

// Handle errors when fetching or initializing a flow
export function handleFlowError<S>(
  navigate: NavigateFunction,
  flowType: FlowType,
  resetFlow: Dispatch<SetStateAction<S | undefined>>,
  showError?: (message: string) => void,
) {
  return async (err: AxiosError<any>) => {
    const errorId = err.response?.data?.error?.id;

    switch (errorId) {
      case 'session_inactive':
        navigate('/login?return_to=' + encodeURIComponent(window.location.pathname));
        return;

      case 'session_aal2_required':
        if (err.response?.data.redirect_browser_to) {
          window.location.href = err.response.data.redirect_browser_to;
          return;
        }
        navigate('/login?aal=aal2&return_to=' + encodeURIComponent(window.location.pathname));
        return;

      case 'session_already_available':
        // User is already signed in
        navigate('/');
        return;

      case 'session_refresh_required':
        // Need re-authentication
        window.location.href = err.response?.data.redirect_browser_to;
        return;

      case 'self_service_flow_return_to_forbidden':
        showError?.('The return address is not allowed.');
        resetFlow(undefined);
        navigate('/' + flowType);
        return;

      case 'self_service_flow_expired':
        showError?.('Your session expired. Please try again.');
        resetFlow(undefined);
        navigate('/' + flowType);
        return;

      case 'security_csrf_violation':
        showError?.('A security error occurred. Please try again.');
        resetFlow(undefined);
        navigate('/' + flowType);
        return;

      case 'security_identity_mismatch':
        resetFlow(undefined);
        navigate('/' + flowType);
        return;

      case 'browser_location_change_required':
        window.location.href = err.response?.data.redirect_browser_to;
        return;
    }

    // Handle by status code
    switch (err.response?.status) {
      case 410:
        // Flow expired
        resetFlow(undefined);
        navigate('/' + flowType);
        return;

      case 403:
        // CSRF or other forbidden error - restart flow
        showError?.('Session error. Please try again.');
        resetFlow(undefined);
        navigate('/' + flowType);
        return;
    }

    // Re-throw unhandled errors
    return Promise.reject(err);
  };
}

// Alias for consistency with reference implementation
export const handleGetFlowError = handleFlowError;
